import { useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, CheckCircle2, ArrowRight, Play } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { AccordionList } from "../components/AccordionList";
import { Footer } from "../sections/Closing";
import { HOST, PORTRAIT_URL, LOGO_URL, scrollToId } from "../lib/content";
import { getAttribution } from "../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REPLAY_VIDEO_URL = ""; // [WEBINAR_REPLAY_VIDEO_URL] — paste hosted replay URL (mp4 or embed)
const REPLAY_WINDOW = "72 HOURS";
const CALLS_THIS_WEEK = 7;

const PURPLE = "#7C3AED";
const DEEP = "#6D28D9";

const STACK = [
  ["Personalized Pre-Approval Snapshot (48-hr turnaround)", "$500"],
  ["Single-Application Broker Model (20+ lenders shopped, one soft + one hard pull)", "$1,200"],
  ["Written Loan Program Match Analysis (FHA vs. conventional vs. USDA)", "$750"],
  ["Proactive Closing Management (weekly status + lender oversight)", "$1,500"],
  ["Free Homebuyer Education Webinar access (recurring)", "$497"],
  ["Bridge Strategy Consultation (for move-up buyers)", "$1,000"],
  ["Same-Day Responsiveness Commitment (direct access to Velisa, not a processor)", "Priceless"],
];

const PATH = [
  { n: "01", t: "PRE-APPROVAL SNAPSHOT", d: "Your real buying power, top loan program, and monthly payment breakdown — a real number, not a range, within 48 hours." },
  { n: "02", t: "LOAN PROGRAM MATCH", d: "Side-by-side comparison of every loan you qualify for — FHA, conventional, USDA, and down payment assistance — with one clear recommendation and the math shown." },
  { n: "03", t: "PROACTIVE CLOSING MANAGEMENT", d: "Weekly status updates and lender-milestone tracking so nothing derails your closing in the final 10 days." },
];

const FAQS = [
  { title: "Is the Buyer Clarity Call really free?", body: "Yes. It's a 30-minute working session — we run your Pre-Approval Snapshot together and you leave with a written roadmap. Nothing is sold and nothing is owed." },
  { title: "Will booking hurt my credit?", body: "No. The call itself requires no credit pull at all. If you later choose to move forward, Velisa's broker model uses one soft pull to shop 20+ lenders — and only one hard pull when you actually apply." },
  { title: "What if the honest answer is \u201cnot yet, keep saving\u201d?", body: "Then that's exactly what you'll hear, along with a written plan for what to fix and when to come back. A clear \u201cnot yet\u201d with a path beats a vague \u201cmaybe\u201d every time." },
  { title: "Do I have to use Velisa for my loan?", body: "No. The roadmap is yours to keep no matter who you work with. There's no obligation and no pressure — the goal of the call is clarity, not commitment." },
  { title: "What if I'm a move-up buyer selling my current home too?", body: "The call includes a Bridge Strategy Consultation — how to time your sale and purchase, and which financing options let you move without owning two mortgages longer than necessary." },
  { title: "How long is the replay available?", body: `The replay stays live for ${REPLAY_WINDOW.toLowerCase()} from your registration, so watch it soon and book your call while the training is fresh.` },
  { title: "What happens after I book?", body: "You'll get a confirmation with your call time and a short prep note. On the call, we run your numbers live, compare programs, and you leave with your written next step — whether you buy in 30 days or 12 months." },
  { title: "What areas does Velisa serve?", body: "Nevada and Texas — fully licensed in both states. If you're outside those markets, book anyway and she'll point you to a trusted resource." },
];

const BookingForm = ({ idPrefix }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", track: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/clarity-call`, {
        ...form,
        videoWatchPct: window.__watchPct || 0,
        consentTimestamp: new Date().toISOString(),
        ...getAttribution(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="glass-panel p-8 text-center" data-testid={`${idPrefix}-booking-success`}>
        <CheckCircle2 className="mx-auto mb-4" size={44} style={{ color: PURPLE }} />
        <h3 className="font-display font-semibold text-xl uppercase tracking-tight mb-2">Your Clarity Call Is Requested</h3>
        <p className="text-sm text-black/60 leading-relaxed">
          Velisa's team will text and email you within one business day to lock in your time. Watch for a message from (702) 608-0721.
        </p>
        <p className="font-mono-c text-[10px] tracking-[0.2em] text-black/40 mt-5">FREE · NO HARD CREDIT PULL · WRITTEN ROADMAP INCLUDED</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${idPrefix}-booking-form`}>
      <div className="grid grid-cols-2 gap-3">
        <input required className="form-input" placeholder="First name" value={form.firstName} onChange={set("firstName")} data-testid={`${idPrefix}-book-first-name`} />
        <input required className="form-input" placeholder="Last name" value={form.lastName} onChange={set("lastName")} data-testid={`${idPrefix}-book-last-name`} />
      </div>
      <input required type="email" className="form-input" placeholder="Email address" value={form.email} onChange={set("email")} data-testid={`${idPrefix}-book-email`} />
      <input required type="tel" className="form-input" placeholder="Mobile phone" value={form.phone} onChange={set("phone")} data-testid={`${idPrefix}-book-phone`} />
      <select required className="form-input" value={form.track} onChange={set("track")} data-testid={`${idPrefix}-book-track`}>
        <option value="" disabled>Which best describes you?</option>
        <option value="First-time buyer">First-time buyer</option>
        <option value="Move-up buyer">Move-up buyer</option>
      </select>
      <button type="submit" disabled={loading} className="btn-accent w-full py-3.5 text-[13px] disabled:opacity-60" data-testid={`${idPrefix}-book-submit`}>
        {loading ? "BOOKING…" : "✓ BOOK MY FREE CLARITY CALL"}
      </button>
      <p className="font-mono-c text-[10px] text-black/40 text-center tracking-wide">Free · No hard credit pull · Written pre-approval roadmap included</p>
    </form>
  );
};

const Player = () => {
  const videoRef = useRef(null);
  const onTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const pct = Math.round((v.currentTime / v.duration) * 100);
    for (const m of [25, 50, 75, 100]) if (pct >= m && (window.__watchPct || 0) < m) window.__watchPct = m;
  };
  return (
    <div className="relative border border-black/15 overflow-hidden" style={{ aspectRatio: "16/9", background: "#0c0a12" }} data-testid="replay-player">
      <div className="corner-mark top-3 left-3 border-t-2 border-l-2 z-10" />
      <div className="corner-mark bottom-3 right-3 border-b-2 border-r-2 z-10" />
      {REPLAY_VIDEO_URL ? (
        <video ref={videoRef} onTimeUpdate={onTime} controls className="w-full h-full" src={REPLAY_VIDEO_URL} data-testid="replay-video" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ background: PURPLE }}>
            <Play size={26} className="text-white ml-1" />
          </div>
          <p className="font-display font-semibold uppercase tracking-tight text-lg text-white">Replay Uploading</p>
          <p className="font-mono-c text-[10px] tracking-[0.25em] text-white/50 mt-2" data-testid="replay-placeholder-note">
            [WEBINAR_REPLAY_VIDEO_URL] — THE SEPT 17 RECORDING DROPS IN HERE
          </p>
        </div>
      )}
    </div>
  );
};

const ReplayNav = () => (
  <div className="fixed top-5 left-0 right-0 z-[100] flex justify-center pointer-events-none px-3">
    <motion.nav
      initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
      className="pointer-events-auto flex items-center gap-1 rounded-full glass-panel px-2 py-1.5" data-testid="replay-nav"
    >
      <div className="hidden sm:flex items-center justify-center bg-white rounded-full w-8 h-8 overflow-hidden shrink-0 ml-0.5">
        <img src={LOGO_URL} alt="Yes We Can Lending" className="w-7 h-7 object-contain" />
      </div>
      {[["WATCH", "watch"], ["THE PATH", "path"], ["HOST", "replay-host"], ["FAQ", "replay-faq"]].map(([l, id]) => (
        <button key={id} onClick={() => scrollToId(id)} className="font-mono-c text-[10px] md:text-[11px] tracking-[0.14em] px-2 md:px-3 py-2 text-black/60 hover:text-black transition-colors duration-200" data-testid={`replay-nav-${id}`}>
          {l}
        </button>
      ))}
      <button onClick={() => scrollToId("book")} className="btn-accent rounded-full text-[10px] md:text-[11px] px-3.5 md:px-4 py-2" data-testid="replay-nav-book">
        BOOK ↗
      </button>
    </motion.nav>
  </div>
);

export default function Replay() {
  return (
    <div data-testid="replay-page">
      <ReplayNav />
      <main>
        {/* HERO */}
        <section id="watch" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" data-testid="replay-hero">
          <div className="glow-orb w-[420px] h-[420px] -top-24 -left-32" style={{ background: "rgba(124,58,237,0.14)" }} />
          <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-[45%_55%] gap-12 items-center">
            <div>
              <p className="font-mono-c text-[10px] tracking-[0.25em] mb-6" style={{ color: DEEP }}>YOUR REPLAY IS READY — WATCH BEFORE IT EXPIRES</p>
              <h1 className="font-display font-bold uppercase" style={{ fontSize: "clamp(32px, 3.4vw, 60px)", letterSpacing: "-0.03em", lineHeight: 0.95 }} data-testid="replay-h1">
                <motion.span className="block" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}>HOW TO BUY YOUR FIRST HOME</motion.span>
                <motion.span className="block" style={{ color: PURPLE }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32, duration: 0.8 }}>WITH TOTAL CLARITY</motion.span>
                <motion.span className="block opacity-60" style={{ fontSize: "62%" }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.44, duration: 0.8 }}>— NO GUESSWORK, NO GHOSTING</motion.span>
              </h1>
              <p className="mt-6 text-[15px] leading-relaxed text-black/60 max-w-lg">
                Watch the full First-Time Buyer Clarity Session below, then book your free Buyer Clarity Call — the same call where we build your Pre-Approval Snapshot, no hard credit pull required.
              </p>
              <blockquote className="mt-6 pl-4 italic text-sm text-black/60 max-w-md" style={{ borderLeft: `2px solid ${PURPLE}` }}>
                The problem was never your income. It's that nobody ever showed you the actual path, in the right order.
              </blockquote>
              <p className="font-mono-c text-[11px] tracking-[0.15em] mt-6" style={{ color: DEEP }} data-testid="replay-window-line">
                ⏳ THIS REPLAY IS AVAILABLE FOR {REPLAY_WINDOW} FROM YOUR REGISTRATION.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <img src={PORTRAIT_URL} alt={HOST.name} className="w-12 h-12 rounded-full object-cover" style={{ border: `2px solid ${PURPLE}` }} />
                <div>
                  <p className="text-sm font-semibold text-[#181818]">{HOST.name}</p>
                  <p className="font-mono-c text-[10px] tracking-[0.15em] text-black/40">Yes We Can Lending Corporation LLC · NMLS# {HOST.nmls}</p>
                </div>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.9 }}>
              <Player />
              <button onClick={() => scrollToId("book")} className="btn-accent w-full py-4 text-[13px] mt-4" data-testid="replay-hero-cta">
                ✓ BOOK MY FREE CLARITY CALL
              </button>
              <p className="font-mono-c text-[10px] text-black/40 text-center tracking-wide mt-2">Free · No hard credit pull · Written pre-approval roadmap included</p>
            </motion.div>
          </div>
        </section>

        {/* TICKER */}
        <section className="border-y border-black/10 py-4 overflow-hidden" data-testid="replay-ticker">
          <div className="flex whitespace-nowrap" style={{ animation: "replay-marquee 30s linear infinite" }}>
            {[0, 1].map((r) => (
              <span key={r} className="font-mono-c text-[11px] tracking-[0.2em] text-black/40">
                {["45-MINUTE TRAINING", "THE CLARITY PATH™", "FREE BUYER CLARITY CALL", "NEVADA & TEXAS", "20+ LENDERS COMPARED", "FHA · CONVENTIONAL · USDA · HELOC"].map((t) => (
                  <span key={t}><span className="mx-6" style={{ color: PURPLE }}>◆</span>{t}</span>
                ))}
              </span>
            ))}
          </div>
          <style>{`@keyframes replay-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        </section>

        {/* MANIFESTO RECAP */}
        <section className="relative py-28 px-6 md:px-12 overflow-hidden" style={{ background: "#F2EEFB" }} data-testid="replay-manifesto">
          <div className="max-w-[1440px] mx-auto">
          <Reveal><p className="chapter-label mb-8">[001] — WHAT YOU JUST LEARNED</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-bold uppercase max-w-4xl" style={{ fontSize: "clamp(34px, 4.2vw, 62px)", lineHeight: 0.97, letterSpacing: "-0.02em" }}>
              <span className="block text-[#181818]">YOU'RE NOT STUCK</span>
              <span className="block text-black/[0.28]">BECAUSE OF YOUR</span>
              <span className="block text-[#181818]">INCOME.</span>
              <span className="block text-black/[0.28]">YOU'RE STUCK BECAUSE OF</span>
              <span className="block" style={{ color: PURPLE }}>CONFLICTING ADVICE.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 text-base leading-[1.75] text-black/60 max-w-[560px]">
              The Zillow number and the bank number were never the same number — and betting on the wrong one is how buyers lose homes they could have won. One wrong loan-program pick can cost $400–$600 every month, or $20K+ more down than you needed. Miss a rate lock and it's roughly $31,500 over 30 years on a $350K loan. That's why the session walked you through the exact order: Pre-Approval Snapshot → Loan Program Match → Proactive Closing Management. Numbers first. Programs second. No surprises in the final 10 days.
            </p>
            <button onClick={() => scrollToId("book")} className="btn-outline-accent mt-8 px-7 py-3.5 text-[12px]" data-testid="replay-manifesto-cta">
              SEE HOW IT APPLIES TO YOU →
            </button>
          </Reveal>
          </div>
        </section>

        {/* CLARITY PATH */}
        <section id="path" className="border-y border-black/10" style={{ background: "#FFFFFF" }} data-testid="clarity-path-section">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-3">
            {PATH.map((s, i) => (
              <div key={s.n} className={`relative p-8 md:p-12 overflow-hidden ${i > 0 ? "md:border-l border-t md:border-t-0 border-black/10" : ""}`} data-testid={`clarity-path-${i}`}>
                <span className="absolute -top-4 -right-2 font-mono-c font-bold text-[120px] leading-none text-black/[0.04] select-none">{s.n}</span>
                <Reveal delay={i * 0.08}>
                  <p className="font-mono-c text-[10px] tracking-[0.3em] mb-3" style={{ color: DEEP }}>STEP {s.n}</p>
                  <h3 className="font-display font-bold uppercase text-xl mb-3">{s.t}</h3>
                  <p className="text-[13px] leading-relaxed text-black/50">{s.d}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </section>

        {/* OFFER STACK */}
        <section className="py-28 px-6 md:px-12" style={{ background: "#F2EEFB" }} data-testid="offer-stack-section">
          <div className="max-w-[900px] mx-auto">
          <Reveal>
            <p className="chapter-label mb-6">[002] — THE OFFER</p>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight mb-12">Here's What's Waiting When You Book</h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="glass-panel p-8 md:p-10">
              <ul className="space-y-5">
                {STACK.map(([item, value], i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                    className="flex items-start justify-between gap-4" data-testid={`stack-item-${i}`}>
                    <span className="flex items-start gap-3 text-[15px] text-black/70">
                      <Check size={17} className="mt-0.5 shrink-0" style={{ color: "#2FA043" }} />
                      {item}
                    </span>
                    <span className="font-mono-c text-[12px] shrink-0" style={{ color: DEEP }}>{value}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="border-t border-black/10 mt-8 pt-6">
                <p className="font-mono-c text-[13px] text-black/40 line-through" data-testid="stack-total">$5,447+ real value</p>
                <p className="font-display font-semibold text-xl mt-2">Your investment today: <span style={{ color: PURPLE }}>$0</span> — just 30 minutes and your honesty.</p>
                <button onClick={() => scrollToId("book")} className="btn-accent mt-6 px-8 py-4 text-[13px]" data-testid="stack-cta">✓ BOOK MY FREE CLARITY CALL</button>
              </div>
            </div>
          </Reveal>
          </div>
        </section>

        {/* GUARANTEE */}
        <section className="py-24 px-6" style={{ background: "#EEF6EF" }} data-testid="guarantee-section">
          <div className="max-w-[760px] mx-auto text-center">
            <Reveal><p className="chapter-label mb-8">[003] — THE PROMISE</p></Reveal>
            <Reveal delay={0.06}>
              <div className="p-10 bg-white" style={{ border: `2px solid ${PURPLE}` }}>
                <h3 className="font-display font-bold uppercase text-2xl mb-5" style={{ color: DEEP }}>The Yes We Can Guarantee</h3>
                <p className="text-[15px] leading-[1.85] text-black/65">
                  Complete the webinar, attend your Clarity Call, and submit your documents within 7 days — and if Velisa can't get you a full pre-approval letter and a clear purchase roadmap within 14 business days, she will personally refer you to the best mortgage resource in your market and pay for a 1-hour consultation with them. No ghosting. No runaround. A real path forward, or Velisa makes it right.
                </p>
              </div>
              <p className="font-mono-c text-[10px] tracking-[0.15em] text-black/40 mt-4">SERVICE GUARANTEE ONLY. NOT A GUARANTEE OF LOAN APPROVAL, RATE, OR CLOSING.</p>
            </Reveal>
          </div>
        </section>

        {/* HOST */}
        <section id="replay-host" className="py-28 px-6 md:px-12" style={{ background: "#FFFFFF" }} data-testid="replay-host-section">
          <div className="max-w-[1300px] mx-auto">
          <Reveal><p className="chapter-label mb-12">[004] — YOUR HOST</p></Reveal>
          <div className="grid lg:grid-cols-[400px_1fr] gap-12 lg:gap-20 items-start">
            <Reveal>
              <div className="relative overflow-hidden border border-black/10" style={{ aspectRatio: "4/5" }}>
                <div className="corner-mark top-3 left-3 border-t-2 border-l-2 z-10" />
                <div className="corner-mark bottom-3 right-3 border-b-2 border-r-2 z-10" />
                <img src={PORTRAIT_URL} alt={HOST.name} className="w-full h-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-bold uppercase text-3xl md:text-[40px] leading-[1.05] tracking-tight mb-8">
                A Broker Who Shops 20+ Lenders. Not a Bank Rep Who Sells One.
              </h2>
              <div className="h-px w-24 mb-8" style={{ background: PURPLE }} />
              <div className="space-y-5 text-[15px] leading-[1.8] text-black/60 max-w-xl">
                <p>I work both Nevada and Texas — two of the fastest-appreciating markets in the country — which means I see twice the loan scenarios, twice the lender behavior, and twice the mistakes buyers get steered into.</p>
                <p>As a broker, I'm not selling one bank's product. Every buyer I work with gets 20+ lenders compared on the same file — one soft pull to shop, one hard pull when you actually apply. The difference shows up in your rate, your fees, and your monthly payment.</p>
                <p>I built the Clarity Path™ after watching too many buyers get ghosted mid-process or pushed into the wrong program because it was easier for the loan officer. Snapshot first, program match second, proactive closing management to the finish line. That's the whole system — and your Clarity Call is where it starts.</p>
              </div>
              <div className="grid grid-cols-2 gap-px mt-10 mb-10 border border-black/10 bg-black/10 max-w-xl" data-testid="replay-host-grid">
                {[["NAME", "Velisa Johnson"], ["COMPANY", "Yes We Can Lending Corporation LLC"], ["NMLS#", "2656519"], ["MARKETS", "Nevada & Texas"]].map(([l, v]) => (
                  <div key={l} className="p-5 bg-white">
                    <p className="font-mono-c text-[9px] tracking-[0.3em] text-black/40 mb-1.5">{l}</p>
                    <p className="text-sm font-semibold text-[#181818]">{v}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => scrollToId("book")} className="btn-accent px-8 py-4 text-[13px]" data-testid="replay-host-cta">✓ BOOK MY FREE CLARITY CALL</button>
            </Reveal>
          </div>
          </div>
        </section>

        {/* OBJECTIONS */}
        <section className="py-28 px-6 md:px-12" style={{ background: "#F2EEFB" }} data-testid="objections-section">
          <div className="max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-24">
            <Reveal>
              <p className="chapter-label mb-6">[005] — STILL DECIDING?</p>
              <h2 className="font-display font-bold uppercase text-3xl md:text-[40px] leading-[1.05] tracking-tight mb-8">Still on the Fence?</h2>
              <div className="space-y-5 text-[15px] leading-[1.8] text-black/60">
                <p>"I don't want to hurt my credit by applying everywhere." You won't — that's the whole point of the single-application broker model. One soft pull shops your file across 20+ lenders. The only hard pull happens once, when you actually apply for the loan you chose.</p>
                <p>"I don't think I can afford anything right now." Maybe — but you're guessing with a Zillow number, not your number. Most buyers who felt priced out discovered their real buying power was different than the internet told them. The Snapshot settles it in 48 hours, for free, either way.</p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="glass-panel p-8 md:p-10 h-full flex flex-col justify-center">
                <p className="font-mono-c text-[11px] tracking-[0.25em] mb-8" style={{ color: DEEP }}>HERE'S WHAT HAPPENS WHEN YOU BOOK:</p>
                <ul className="space-y-6">
                  {["We run your Pre-Approval Snapshot together, live", "You see every loan program you qualify for — not just one bank's product", "You leave with a written roadmap, whether you buy in 30 days or 12 months"].map((t, i) => (
                    <li key={i} className="flex items-start gap-4" data-testid={`booking-benefit-${i}`}>
                      <ArrowRight size={18} className="mt-1 shrink-0" style={{ color: PURPLE }} />
                      <span className="text-[15px] leading-relaxed text-black/70">{t}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => scrollToId("book")} className="btn-accent mt-10 px-8 py-4 text-[13px] self-start" data-testid="objections-cta">✓ BOOK MY FREE CLARITY CALL</button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* FAQ */}
        <section id="replay-faq" className="py-28 px-6 md:px-12" style={{ background: "#FFFFFF" }} data-testid="replay-faq-section">
          <div className="max-w-[1000px] mx-auto">
          <Reveal>
            <p className="chapter-label mb-6">[007] — COMMON QUESTIONS</p>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight mb-14">Common Questions</h2>
          </Reveal>
          <Reveal delay={0.1}><AccordionList items={FAQS} testPrefix="replay-faq" /></Reveal>
          </div>
        </section>

        {/* FINAL BOOKING */}
        <section id="book" className="relative py-28 px-6 md:px-12 overflow-hidden" style={{ background: "#6D28D9" }} data-testid="booking-section">
          <div className="absolute left-[-15%] top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.09), transparent 70%)" }} />
          <div className="relative max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
            <Reveal>
              <p className="chapter-label mb-8" style={{ color: "rgba(255,255,255,0.65)" }}>[008] — YOUR NEXT STEP</p>
              <h2 className="font-display font-bold uppercase tracking-tight text-white" style={{ fontSize: "clamp(34px, 4.2vw, 58px)", lineHeight: 1.0 }} data-testid="booking-h2">
                Get Pre-Approved. Buy With <span style={{ color: "#C4B5FD" }}>Clarity. No Guesswork.</span>
              </h2>
              <ul className="mt-10 space-y-4">
                {["Free — nothing sold, nothing owed", "No hard credit pull", "Written pre-approval roadmap", `${CALLS_THIS_WEEK} Clarity Calls available this week`].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-white/80" data-testid={`booking-bullet-${i}`}>
                    <Check size={17} className="mt-0.5 shrink-0" style={{ color: "#2FA043" }} />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="glass-panel p-4 md:p-6 bg-white" data-testid="booking-card">
                <p className="font-mono-c text-[10px] tracking-[0.3em] mb-1.5 px-2 pt-2" style={{ color: DEEP }}>THE BUYER CLARITY CALL</p>
                <h3 className="font-display font-semibold text-lg uppercase tracking-tight mb-4 px-2">Pick Your Time. Free. 30 Minutes.</h3>
                <iframe
                  src="https://api.leadconnectorhq.com/widget/booking/5VCTYlCcd6Zst7cMKA7L"
                  style={{ width: "100%", border: "none", overflow: "hidden", minHeight: 700 }}
                  scrolling="no"
                  id="5VCTYlCcd6Zst7cMKA7L_booking"
                  title="Book your Buyer Clarity Call"
                  data-testid="booking-calendar-embed"
                />
              </div>
            </Reveal>
          </div>
        </section>

        <Footer />
      </main>

      <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden p-3 glass-panel border-t border-black/10" data-testid="replay-mobile-cta">
        <button onClick={() => scrollToId("book")} className="btn-accent w-full py-4 text-[13px]" data-testid="replay-mobile-cta-button">
          ✓ BOOK MY FREE CLARITY CALL
        </button>
      </div>
    </div>
  );
}
